import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, SlidersHorizontal, Clock, RefreshCw, Sparkles,
  AlertCircle, X, ChevronDown, Flame, MapPin, Loader
} from 'lucide-react';
import FoodCard from '../../components/FoodCard';
import HeroMap from '../../components/HeroMap';
import { useRecommendations } from '../../hooks/useRecommendations';
import { useLocation } from '../../contexts/LocationContext';
import { useActivityTracker } from '../../hooks/useActivityTracker';

/* ── Skeleton — matches Promote page card style ── */
const CardSkeleton = () => (
  <div className="bg-white rounded-[20px] overflow-hidden animate-pulse">
    <div className="aspect-[4/3] md:aspect-[16/10] bg-gray-100" />
    <div className="p-5 space-y-3">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-gray-100" />
        <div className="flex-1 space-y-1.5"><div className="h-4 bg-gray-100 rounded-full w-1/3" /><div className="h-3 bg-gray-100 rounded-full w-1/4" /></div>
      </div>
      <div className="h-3 bg-gray-100 rounded-full w-full" />
      <div className="h-3 bg-gray-100 rounded-full w-2/3" />
      <div className="h-11 bg-gray-100 rounded-2xl w-full mt-2" />
    </div>
  </div>
);

/* ── Filter chip arrays ── */
const VIBES = ['All Vibes', 'Energizing', 'Light & Fresh', 'Quick Bite', 'Filling', 'Comfort', 'Snacky', 'Celebrate', 'Indulgent', 'Warm & Cozy', 'Cool & Chill', 'Surprise Me'];
const CUISINES = ['All Cuisines', 'Italian', 'Chinese', 'Japanese', 'Indian', 'Mexican', 'Thai', 'American', 'French', 'Mediterranean'];
const DIETS = ['All Diets', 'Vegetarian', 'Vegan', 'Gluten-Free', 'Dairy-Free', 'Halal', 'Kosher', 'Keto', 'Paleo'];
const SORTS = [
  { key: 'recommended', label: 'Recommended' },
  { key: 'rating', label: 'Highest Rated' },
  { key: 'reviews', label: 'Most Reviewed' },
];
const DISTANCES = [
  { label: 'Walking (1 km)', value: '1' },
  { label: 'Biking (3 km)', value: '3' },
  { label: 'Driving (5 km)', value: '5' },
  { label: 'Any Distance', value: 'all' },
];
const PRICES = ['$', '$$', '$$$', '$$$$'];
const COMFORTS = [
  { label: 'Dine-Out', value: 'dine-out' },
  { label: 'Order-In', value: 'order-in' },
  { label: 'Prepare It', value: 'prepare-it' },
];

const DineOut = ({ preferences, metrics, onMetricRefresh }) => {
  const { locationString } = useLocation();
  const { track } = useActivityTracker();
  const navigate = useNavigate();

  const [sortBy, setSortBy] = useState('recommended');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedVibe, setSelectedVibe] = useState(preferences?.vibe || 'all');
  const [selectedCuisine, setSelectedCuisine] = useState(preferences?.cuisine || 'all');
  const [selectedDietary, setSelectedDietary] = useState(preferences?.dietary || 'all');
  const [selectedComfort, setSelectedComfort] = useState(preferences?.comfort || 'dine-out');
  const [selectedPrice, setSelectedPrice] = useState([]);
  const [selectedDistance, setSelectedDistance] = useState('all');
  const [openNow, setOpenNow] = useState(false);
  const [likedDishes, setLikedDishes] = useState([]);
  const [highlightId, setHighlightId] = useState(null);

  const { recommendations, aiInsight, loading, aiUpdating, isQuick, error, refetch, hasCache, loadingMore, hasMore, loadMore } = useRecommendations({
    comfort: selectedComfort,
    vibe: selectedVibe,
    cuisine: selectedCuisine,
    dietary: selectedDietary,
    location: preferences?.location || null,
    distance: selectedDistance,
    openNow,
    priceRange: selectedPrice,
    deliveryOptions:
      selectedComfort === 'dine-in' ? ['delivery']
        : selectedComfort === 'cook-at-home' ? ['recipe', 'grocery']
        : selectedComfort === 'meal-plan' ? ['meal-plan', 'meal-kit']
        : ['dine-in', 'pickup'],
    easeOfAccess: [],
  });

  const buildPayload = () => ({
    comfort: selectedComfort,
    vibe: selectedVibe,
    cuisine: selectedCuisine,
    dietary: selectedDietary,
    location: preferences?.location || null,
    distance: selectedDistance,
    openNow,
    priceRange: selectedPrice,
    deliveryOptions:
      selectedComfort === 'dine-in' ? ['delivery']
        : selectedComfort === 'cook-at-home' ? ['recipe', 'grocery']
        : selectedComfort === 'meal-plan' ? ['meal-plan', 'meal-kit']
        : ['dine-in', 'pickup'],
    easeOfAccess: [],
  });

  const handleApplyFilters = () => {
    track('search', { comfort: selectedComfort, vibe: selectedVibe !== 'all' ? selectedVibe : null, cuisine: selectedCuisine !== 'all' ? selectedCuisine : null, location: locationString });
    refetch(buildPayload());
    setShowFilters(false);
  };

  const toggleLike = (dishId) => setLikedDishes(prev => prev.includes(dishId) ? prev.filter(id => id !== dishId) : [...prev, dishId]);
  const togglePrice = (p) => setSelectedPrice(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]);
  const activeFilterCount = [selectedVibe, selectedCuisine, selectedDietary].filter(v => v !== 'all').length + selectedPrice.length + (openNow ? 1 : 0) + (selectedDistance !== 'all' ? 1 : 0);

  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-20 md:pb-8" data-testid="recommendations-page">

      {/* ── Hero — Interactive Google Map with dish tags ── */}
      <div className="relative" style={{ minHeight: '280px' }}>
        {/* Fallback hero image behind the map */}
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=1400&q=80')" }} />
        <HeroMap
          recommendations={recommendations}
          locationString={locationString}
          onMarkerClick={(id) => {
            setHighlightId(id);
            document.getElementById(`dish-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }}
          highlightId={highlightId}
        />
      </div>

      {/* Sticky Tab Bar — sorts on left, Filters + Refresh on right */}
      <div className="sticky top-0 z-30 bg-white/95 backdrop-blur-lg border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center justify-between py-2.5">
            <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide">
              {SORTS.map(s => (
                <button key={s.key} onClick={() => setSortBy(s.key)}
                  className={`px-4 py-2 text-sm font-medium whitespace-nowrap rounded-full transition-all duration-200 ${
                    sortBy === s.key ? 'bg-gray-900 text-white shadow-sm' : 'bg-transparent text-gray-500 hover:bg-gray-100'
                  }`} data-testid={`sort-${s.key}`}>
                  {s.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0 ml-3">
              <button onClick={() => setShowFilters(true)}
                className={`h-9 px-3.5 rounded-full flex items-center gap-1.5 text-xs font-semibold transition-all ${
                  activeFilterCount > 0
                    ? 'bg-[#FF6B35] text-white shadow-sm'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`} data-testid="filter-toggle-btn">
                <SlidersHorizontal className="w-3.5 h-3.5" />Filters
                {activeFilterCount > 0 && <span className="bg-white text-[#FF6B35] text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">{activeFilterCount}</span>}
              </button>
              <button onClick={() => refetch(buildPayload())} disabled={loading || aiUpdating}
                className="h-9 px-3.5 bg-[#FF6B35] hover:bg-[#E5592E] text-white rounded-full flex items-center gap-1.5 font-semibold text-xs shadow-sm active:scale-95 transition-all" data-testid="refresh-recommendations-btn">
                <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />Refresh Picks
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Content ── */}
      <div className="max-w-6xl mx-auto px-4 py-6 md:py-8">
        <div className="flex gap-8">
          {/* Main feed column */}
          <main className="flex-1 min-w-0 max-w-2xl mx-auto lg:mx-0">

        {/* AI Insight / status banners */}
        {aiInsight && !loading && !aiUpdating && (
          <div data-testid="ai-insight-banner" className="mb-5 p-4 rounded-2xl bg-white border border-gray-100 shadow-[0_1px_3px_rgba(0,0,0,0.04)] flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-[#FF6B35] flex-shrink-0 mt-0.5" />
            <p className="text-[15px] text-gray-700 italic leading-relaxed">"{aiInsight}"</p>
          </div>
        )}
        {isQuick && aiUpdating && (
          <div data-testid="quick-recommendations-banner" className="mb-5 p-4 rounded-2xl bg-white border border-gray-100 flex items-center gap-3">
            <div className="w-5 h-5 border-2 border-[#FF6B35]/30 border-t-[#FF6B35] rounded-full animate-spin flex-shrink-0" />
            <span className="text-sm text-gray-500">Instant picks loaded — personalizing with AI<span className="animate-pulse">...</span></span>
          </div>
        )}
        {!loading && !aiUpdating && hasCache && !aiInsight && (
          <div className="mb-5 p-4 rounded-2xl bg-white border border-gray-100 flex items-center justify-between gap-3">
            <span className="text-sm text-gray-400">Showing last search results</span>
            <button onClick={() => refetch(buildPayload())} className="text-sm text-[#FF6B35] font-medium flex items-center gap-1 hover:underline">
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
          </div>
        )}
        {error && (
          <div className="mb-5 p-4 rounded-2xl bg-red-50 border border-red-200 flex items-center gap-2 text-red-700 text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {error} — <button onClick={() => refetch(buildPayload())} className="underline">Retry</button>
          </div>
        )}
        {loading && recommendations.length === 0 && (
          <div className="mb-5 p-4 rounded-2xl bg-white border border-gray-100 flex items-center gap-3">
            <div className="w-5 h-5 border-2 border-[#FF6B35]/30 border-t-[#FF6B35] rounded-full animate-spin flex-shrink-0" />
            <span className="text-sm text-gray-500">Loading quick picks<span className="animate-pulse">...</span></span>
          </div>
        )}

        {/* Feed cards */}
        <div className="space-y-5">
          {loading && recommendations.length === 0
            ? [1, 2, 3].map(i => <CardSkeleton key={i} />)
            : recommendations.map(dish => (
                <div key={dish.id} id={`dish-${dish.id}`}
                  onMouseEnter={() => setHighlightId(dish.id)}
                  onMouseLeave={() => setHighlightId(null)}>
                  <FoodCard variant="recommendation"
                    dish={dish}
                    mode={selectedComfort}
                  />
                </div>
              ))
          }
        </div>

        {/* Infinite scroll: Load More */}
        {recommendations.length > 0 && (
          <div className="py-6 flex flex-col items-center gap-3">
            {loadingMore ? (
              <div className="flex items-center gap-2 text-gray-500">
                <Loader className="w-5 h-5 animate-spin text-[#FF6B35]" />
                <span className="text-sm">Loading more picks...</span>
              </div>
            ) : hasMore ? (
              <button onClick={() => loadMore(buildPayload())}
                className="h-11 px-8 bg-gray-900 text-white font-semibold text-sm rounded-2xl hover:bg-gray-800 active:scale-[0.98] transition-all flex items-center gap-2"
                data-testid="load-more-btn">
                <RefreshCw className="w-4 h-4" />Load More Picks
              </button>
            ) : (
              <p className="text-sm text-gray-400">You've seen all current picks!</p>
            )}
          </div>
        )}
          </main>

          {/* Sidebar (desktop) — X.com style */}
          <aside className="hidden lg:flex flex-col w-72 xl:w-80 flex-shrink-0 gap-5">
            {aiInsight && !loading && !aiUpdating && (
              <div className="bg-white rounded-[20px] p-5 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
                <h3 className="font-bold text-gray-900 text-sm mb-3 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-[#FF6B35]" /> AI Insight
                </h3>
                <p className="text-sm text-gray-600 italic leading-relaxed">"{aiInsight}"</p>
              </div>
            )}
            <div className="bg-white rounded-[20px] p-5 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
              <h3 className="font-bold text-gray-900 text-sm mb-3">Quick Vibes</h3>
              <div className="flex flex-wrap gap-2">
                {['Energizing', 'Comfort', 'Quick Bite', 'Indulgent', 'Fresh & Light', 'Celebrate'].map(vibe => (
                  <button key={vibe} onClick={() => { setSelectedVibe(vibe.toLowerCase().replace(/ & /g, '-').replace(/ /g, '-')); refetch(buildPayload()); }}
                    className={`px-3.5 py-1.5 text-xs font-medium rounded-full transition-all duration-200 ${
                      selectedVibe === vibe.toLowerCase().replace(/ & /g, '-').replace(/ /g, '-') ? 'bg-gray-900 text-white' : 'bg-gray-50 hover:bg-gray-900 hover:text-white text-gray-600'
                    }`}>
                    {vibe}
                  </button>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-[20px] p-5 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
              <h3 className="font-bold text-gray-900 text-sm mb-3">Popular Cuisines</h3>
              <div className="flex flex-wrap gap-2">
                {['Italian', 'Japanese', 'Mexican', 'Indian', 'Thai', 'Chinese'].map(c => (
                  <button key={c} onClick={() => { setSelectedCuisine(c.toLowerCase()); refetch(buildPayload()); }}
                    className={`px-3.5 py-1.5 text-xs font-medium rounded-full transition-all duration-200 ${
                      selectedCuisine === c.toLowerCase() ? 'bg-gray-900 text-white' : 'bg-gray-50 hover:bg-gray-900 hover:text-white text-gray-600'
                    }`}>
                    {c}
                  </button>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════
          FILTER DRAWER — bottom sheet on mobile, side panel on desktop
          ══════════════════════════════════════════════════════════════ */}
      {showFilters && (
        <>
          <div className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm" onClick={() => setShowFilters(false)} />
          <div className="fixed inset-y-0 right-0 z-50 w-full max-w-sm bg-white shadow-2xl overflow-y-auto" data-testid="filter-drawer">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-4 flex items-center justify-between z-10">
              <h3 className="font-bold text-lg text-gray-900 flex items-center gap-2">
                <SlidersHorizontal className="w-5 h-5 text-[#FF6B35]" /> Filters
              </h3>
              <button onClick={() => setShowFilters(false)} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <div className="p-5 space-y-6">
              {/* Open Now */}
              <label className="flex items-center gap-3 cursor-pointer">
                <div className={`w-10 h-6 rounded-full transition-colors flex items-center px-0.5 ${openNow ? 'bg-[#FF6B35]' : 'bg-gray-200'}`}>
                  <div className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${openNow ? 'translate-x-4' : ''}`} />
                </div>
                <Clock className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-900">Open Now</span>
              </label>

              {/* Comfort */}
              <div>
                <h4 className="font-semibold text-gray-900 text-sm mb-3">Comfort</h4>
                <div className="flex flex-wrap gap-2">
                  {COMFORTS.map(c => (
                    <button
                      key={c.value}
                      onClick={() => setSelectedComfort(c.value)}
                      className={`px-4 py-2 text-sm font-medium rounded-full transition-all ${
                        selectedComfort === c.value
                          ? 'bg-gray-900 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Vibe */}
              <div>
                <h4 className="font-semibold text-gray-900 text-sm mb-3">Vibe</h4>
                <div className="flex flex-wrap gap-2">
                  {VIBES.map(vibe => {
                    const val = vibe === 'All Vibes' ? 'all' : vibe.toLowerCase().replace(/ & /g, '-').replace(/ /g, '-');
                    return (
                      <button
                        key={vibe}
                        onClick={() => setSelectedVibe(val)}
                        className={`px-3.5 py-1.5 text-xs font-medium rounded-full transition-all ${
                          selectedVibe === val
                            ? 'bg-gray-900 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {vibe}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Cuisine */}
              <div>
                <h4 className="font-semibold text-gray-900 text-sm mb-3">Cuisine</h4>
                <div className="flex flex-wrap gap-2">
                  {CUISINES.map(c => {
                    const val = c === 'All Cuisines' ? 'all' : c.toLowerCase();
                    return (
                      <button
                        key={c}
                        onClick={() => setSelectedCuisine(val)}
                        className={`px-3.5 py-1.5 text-xs font-medium rounded-full transition-all ${
                          selectedCuisine === val
                            ? 'bg-gray-900 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {c}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Diet */}
              <div>
                <h4 className="font-semibold text-gray-900 text-sm mb-3">Dietary</h4>
                <div className="flex flex-wrap gap-2">
                  {DIETS.map(d => {
                    const val = d === 'All Diets' ? 'all' : d.toLowerCase().replace(/-/g, '');
                    return (
                      <button
                        key={d}
                        onClick={() => setSelectedDietary(val)}
                        className={`px-3.5 py-1.5 text-xs font-medium rounded-full transition-all ${
                          selectedDietary === val
                            ? 'bg-gray-900 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {d}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Price */}
              <div>
                <h4 className="font-semibold text-gray-900 text-sm mb-3">Price</h4>
                <div className="flex gap-2">
                  {PRICES.map(p => (
                    <button
                      key={p}
                      onClick={() => togglePrice(p)}
                      className={`flex-1 py-2.5 text-sm font-medium rounded-full transition-all ${
                        selectedPrice.includes(p)
                          ? 'bg-gray-900 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>

              {/* Distance */}
              <div>
                <h4 className="font-semibold text-gray-900 text-sm mb-3">Distance</h4>
                <div className="flex flex-wrap gap-2">
                  {DISTANCES.map(d => (
                    <button
                      key={d.value}
                      onClick={() => setSelectedDistance(d.value)}
                      className={`px-3.5 py-1.5 text-xs font-medium rounded-full transition-all ${
                        selectedDistance === d.value
                          ? 'bg-gray-900 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {d.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Sticky Apply */}
            <div className="sticky bottom-0 bg-white border-t border-gray-100 p-5">
              <button
                onClick={handleApplyFilters}
                disabled={loading || aiUpdating}
                className="w-full py-3.5 bg-gray-900 text-white font-bold text-sm rounded-full hover:bg-gray-800 active:scale-[0.98] transition-all disabled:opacity-50"
                data-testid="apply-filters-btn"
              >
                {loading ? 'Getting AI Picks...' : 'Get AI Picks'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DineOut;
